import { type FC } from 'react';
import { useFragment, graphql } from 'react-relay';
import classnames from 'classnames';
import { useSplit } from 'dibs-crypto-utils/exports/useSplit';
import { NftAvatarCircle } from 'dibs-crypto-utils/exports/NftAvatarCircle';
import { type Recipients_address$key } from './__generated__/Recipients_address.graphql';
import { type Recipients_paymentAddresses$key } from './__generated__/Recipients_paymentAddresses.graphql';

import styles from './Recipients.scss';

const DISPLAYED_AVATARS_COUNT = 2;

type Props = {
    singleAddress?: Recipients_address$key | null | undefined;
    addressList?: Recipients_paymentAddresses$key | null | undefined;
    recipientsOverride?: Array<{ allocation: string; address: string; avatarImage?: string }>;
    onBanner?: boolean;
};

export const Recipients: FC<Props> = ({
    singleAddress: singleAddressRef = null,
    addressList: addressListRef = null,
    recipientsOverride = null,
    onBanner = false,
}) => {
    const singleAddress = useFragment(
        graphql`
            fragment Recipients_address on EthereumAddressType {
                ...useSplit_address
            }
        `,
        singleAddressRef
    );
    const addressList = useFragment(
        graphql`
            fragment Recipients_paymentAddresses on PaymentAddresses {
                ...useSplit_paymentAddresses
            }
        `,
        addressListRef
    );
    const { normalizedAllocations } = useSplit({ singleAddress, addressList });

    let recipients: Array<{
        key: string;
        address: string;
        avatarImage?: string;
        allocation: string;
    }> = [];

    if (recipientsOverride) {
        recipients = recipientsOverride.map(recipient => ({
            key: recipient.address,
            address: recipient.address,
            avatarImage: recipient.avatarImage,
            allocation: recipient.allocation,
        }));
    } else {
        recipients = normalizedAllocations.map(recipient => ({
            key: recipient.address?.address || '',
            address: recipient.address?.address || '',
            avatarImage: recipient?.address?.publicProfile?.avatarImage?.path || '',
            allocation: `${recipient?.allocation}`,
        }));
    }

    return (
        <div className={styles.recipientsContainer}>
            <div className={styles.avatarsContainer}>
                {recipients
                    .sort((a, b) => +(b.allocation ?? 0) - +(a.allocation ?? 0))
                    .slice(0, DISPLAYED_AVATARS_COUNT)
                    .reverse()
                    .map(recipient => (
                        <NftAvatarCircle
                            key={recipient.key}
                            addressOverride={{
                                address: recipient.address,
                                avatarImage: recipient.avatarImage,
                            }}
                            wrapperClassName={classnames(styles.avatarWrapper, {
                                [styles.onBanner]: onBanner,
                            })}
                        />
                    ))}
            </div>
            {normalizedAllocations.length > DISPLAYED_AVATARS_COUNT && (
                <span className={styles.recipientsCount}>
                    {normalizedAllocations.length - DISPLAYED_AVATARS_COUNT}
                </span>
            )}
        </div>
    );
};
